import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesConfigAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    Settings,
    Users,
    Shield,
    ArrowLeft,
    Plus,
    Trash2,
    Loader2,
    UserPlus,
    Check,
    X,
    Settings2,
    Plug,
} from 'lucide-react';

export default function OrdenesConfigPage() {
    const navigate = useNavigate();
    const { tienePermiso, esAdmin, loading: permisosLoading } = useOrdenesPermisos();
    const [activeTab, setActiveTab] = useState('roles');
    const [loading, setLoading] = useState(true);
    
    // Data
    const [roles, setRoles] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [permisosDisponibles, setPermisosDisponibles] = useState([]);
    
    // Dialogs
    const [showAsignarDialog, setShowAsignarDialog] = useState(false);
    const [showCreateRolDialog, setShowCreateRolDialog] = useState(false);
    const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
    
    // Form state
    const [selectedUsuario, setSelectedUsuario] = useState(null);
    const [selectedRol, setSelectedRol] = useState('');
    const [nuevoRol, setNuevoRol] = useState({ codigo: '', nombre: '', descripcion: '', permisos: {} });
    const [nuevoUsuario, setNuevoUsuario] = useState({ nombre: '', email: '', password: '', rol: 'usuario' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [rolesRes, usuariosRes, permisosRes] = await Promise.all([
                ordenesConfigAPI.getRoles(),
                ordenesConfigAPI.getUsuarios(),
                ordenesConfigAPI.getPermisosDisponibles(),
            ]);
            setRoles(rolesRes.data);
            setUsuarios(usuariosRes.data);
            setPermisosDisponibles(permisosRes.data.permisos || []);
        } catch (err) {
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAsignar = (usuario) => {
        setSelectedUsuario(usuario);
        setSelectedRol(usuario.rol_ordenes || '');
        setShowAsignarDialog(true);
    };

    const handleAsignarRol = async () => {
        if (!selectedRol) {
            toast.error('Selecciona un rol');
            return;
        }
        
        setSaving(true);
        try {
            await ordenesConfigAPI.asignarRol(selectedUsuario.id, selectedRol);
            toast.success('Rol asignado correctamente');
            setShowAsignarDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al asignar rol');
        } finally {
            setSaving(false);
        }
    };

    const handleQuitarRol = async (usuario) => {
        if (!confirm(`¿Quitar rol a ${usuario.nombre}?`)) return;
        
        try {
            await ordenesConfigAPI.quitarRol(usuario.id);
            toast.success('Rol removido');
            loadData();
        } catch (err) {
            toast.error('Error al quitar rol');
        }
    };

    const handleTogglePermiso = (codigo) => {
        setNuevoRol(prev => ({
            ...prev,
            permisos: {
                ...prev.permisos,
                [codigo]: !prev.permisos[codigo]
            }
        }));
    };

    const handleCreateRol = async () => {
        if (!nuevoRol.codigo || !nuevoRol.nombre) {
            toast.error('Código y nombre son requeridos');
            return;
        }
        
        setSaving(true);
        try {
            await ordenesConfigAPI.createRol(nuevoRol);
            toast.success('Rol creado correctamente');
            setShowCreateRolDialog(false);
            setNuevoRol({ codigo: '', nombre: '', descripcion: '', permisos: {} });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al crear rol');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateUser = async () => {
        if (!nuevoUsuario.nombre || !nuevoUsuario.email || !nuevoUsuario.password) {
            toast.error('Todos los campos son requeridos');
            return;
        }

        setSaving(true);
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/usuarios`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(nuevoUsuario)
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.detail || 'Error al crear usuario');
            }

            toast.success('Usuario creado correctamente');
            setShowCreateUserDialog(false);
            setNuevoUsuario({ nombre: '', email: '', password: '', rol: 'usuario' });
            loadData();
        } catch (err) {
            toast.error(err.message || 'Error al crear usuario');
        } finally {
            setSaving(false);
        }
    };

    const getRolNombre = (codigo) => {
        const rol = roles.find(r => r.codigo === codigo);
        return rol?.nombre || codigo;
    };

    if (permisosLoading || loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!esAdmin && !tienePermiso('gestionar_roles')) {
        return (
            <div className="p-6 text-center">
                <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-bold mb-2">Acceso Restringido</h2>
                <p className="text-muted-foreground">No tienes permisos para acceder a esta sección.</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/ordenes/dashboard')}>
                    Volver al Dashboard
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Settings className="h-6 w-6" />
                        Configuración del Módulo
                    </h1>
                    <p className="text-muted-foreground">Gestión de roles, permisos y usuarios</p>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="roles" className="gap-2">
                        <Shield className="h-4 w-4" />
                        Roles
                    </TabsTrigger>
                    <TabsTrigger value="usuarios" className="gap-2">
                        <Users className="h-4 w-4" />
                        Usuarios
                    </TabsTrigger>
                    <TabsTrigger value="campos" className="gap-2" onClick={() => navigate('/ordenes/configuracion/campos')}>
                        <Settings2 className="h-4 w-4" />
                        Campos
                    </TabsTrigger>
                    <TabsTrigger value="notificaciones" className="gap-2" onClick={() => navigate('/ordenes/configuracion/notificaciones')}>
                        <Shield className="h-4 w-4" />
                        Emails
                    </TabsTrigger>
                    <TabsTrigger value="integraciones" className="gap-2" onClick={() => navigate('/ordenes/integraciones/4sat')}>
                        <Plug className="h-4 w-4" />
                        Integraciones
                    </TabsTrigger>
                </TabsList>

                {/* Roles Tab */}
                <TabsContent value="roles" className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold">Roles Disponibles</h2>
                        <Button onClick={() => setShowCreateRolDialog(true)} data-testid="create-rol-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Crear Rol Personalizado
                        </Button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {roles.map((rol) => (
                            <Card key={rol.codigo} className="border-2">
                                <CardHeader className="pb-2">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <CardTitle className="text-base">{rol.nombre}</CardTitle>
                                            <CardDescription className="text-xs font-mono">{rol.codigo}</CardDescription>
                                        </div>
                                        {rol.predefinido && (
                                            <Badge variant="secondary">Predefinido</Badge>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-2">
                                        {Object.values(rol.permisos || {}).filter(Boolean).length} permisos activos
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {Object.entries(rol.permisos || {}).filter(([_, v]) => v).slice(0, 5).map(([key]) => (
                                            <Badge key={key} variant="outline" className="text-xs">
                                                {key.replace(/_/g, ' ')}
                                            </Badge>
                                        ))}
                                        {Object.values(rol.permisos || {}).filter(Boolean).length > 5 && (
                                            <Badge variant="outline" className="text-xs">
                                                +{Object.values(rol.permisos || {}).filter(Boolean).length - 5} más
                                            </Badge>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                {/* Usuarios Tab */}
                <TabsContent value="usuarios" className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Usuarios y Asignaciones</h2>
                        <Button onClick={() => setShowCreateUserDialog(true)} data-testid="create-user-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Nuevo Usuario
                        </Button>
                    </div>

                    <Card className="border-2">
                        <CardContent className="p-0 overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Usuario</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Rol Sistema</TableHead>
                                        <TableHead>Rol Órdenes</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {usuarios.map((usuario) => (
                                        <TableRow key={usuario.id} data-testid={`user-${usuario.id}`}>
                                            <TableCell className="font-medium">{usuario.nombre}</TableCell>
                                            <TableCell className="text-muted-foreground">{usuario.email}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{usuario.rol}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {usuario.rol_ordenes ? (
                                                    <Badge className="bg-blue-100 text-blue-700">
                                                        {getRolNombre(usuario.rol_ordenes)}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">Sin asignar</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={() => handleOpenAsignar(usuario)}
                                                        title="Asignar rol"
                                                    >
                                                        <UserPlus className="h-4 w-4" />
                                                    </Button>
                                                    {usuario.rol_ordenes && (
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            onClick={() => handleQuitarRol(usuario)}
                                                            title="Quitar rol"
                                                        >
                                                            <X className="h-4 w-4 text-red-500" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Asignar Rol Dialog */}
            <Dialog open={showAsignarDialog} onOpenChange={setShowAsignarDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Asignar Rol</DialogTitle>
                        <DialogDescription>
                            Asignar rol del módulo Órdenes de Servicio a <strong>{selectedUsuario?.nombre}</strong>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Rol</Label>
                            <Select value={selectedRol} onValueChange={setSelectedRol}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar rol" />
                                </SelectTrigger>
                                <SelectContent>
                                    {roles.map(rol => (
                                        <SelectItem key={rol.codigo} value={rol.codigo}>{rol.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {selectedRol && (
                            <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm font-medium mb-2">Permisos del rol:</p>
                                <div className="flex flex-wrap gap-1">
                                    {Object.entries(roles.find(r => r.codigo === selectedRol)?.permisos || {})
                                        .filter(([_, v]) => v)
                                        .map(([key]) => (
                                            <Badge key={key} variant="outline" className="text-xs">
                                                {key.replace(/_/g, ' ')}
                                            </Badge>
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAsignarDialog(false)}>Cancelar</Button>
                        <Button onClick={handleAsignarRol} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Asignar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Rol Dialog */}
            <Dialog open={showCreateRolDialog} onOpenChange={setShowCreateRolDialog}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Crear Rol Personalizado</DialogTitle>
                        <DialogDescription>
                            Define un nuevo rol con permisos específicos
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Código *</Label>
                                <Input
                                    value={nuevoRol.codigo}
                                    onChange={(e) => setNuevoRol({...nuevoRol, codigo: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                                    placeholder="mi_rol_custom"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nombre *</Label>
                                <Input
                                    value={nuevoRol.nombre}
                                    onChange={(e) => setNuevoRol({...nuevoRol, nombre: e.target.value})}
                                    placeholder="Mi Rol Personalizado"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Descripción</Label>
                            <Input
                                value={nuevoRol.descripcion}
                                onChange={(e) => setNuevoRol({...nuevoRol, descripcion: e.target.value})}
                                placeholder="Descripción del rol"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Permisos</Label>
                            <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {permisosDisponibles.map((permiso) => (
                                        <div key={permiso.codigo} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={permiso.codigo}
                                                checked={nuevoRol.permisos[permiso.codigo] || false}
                                                onCheckedChange={() => handleTogglePermiso(permiso.codigo)}
                                            />
                                            <label 
                                                htmlFor={permiso.codigo} 
                                                className="text-sm cursor-pointer"
                                                title={permiso.descripcion}
                                            >
                                                {permiso.descripcion}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {Object.values(nuevoRol.permisos).filter(Boolean).length} permisos seleccionados
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateRolDialog(false)}>Cancelar</Button>
                        <Button onClick={handleCreateRol} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Crear Rol
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Crear Usuario Dialog */}
            <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Crear Nuevo Usuario</DialogTitle>
                        <DialogDescription>
                            El usuario será creado en tu empresa y podrás asignarle roles después.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nombre</Label>
                            <Input
                                value={nuevoUsuario.nombre}
                                onChange={(e) => setNuevoUsuario({...nuevoUsuario, nombre: e.target.value})}
                                placeholder="Nombre completo"
                                data-testid="new-user-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={nuevoUsuario.email}
                                onChange={(e) => setNuevoUsuario({...nuevoUsuario, email: e.target.value})}
                                placeholder="correo@ejemplo.com"
                                data-testid="new-user-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Contraseña</Label>
                            <Input
                                type="password"
                                value={nuevoUsuario.password}
                                onChange={(e) => setNuevoUsuario({...nuevoUsuario, password: e.target.value})}
                                placeholder="Mínimo 6 caracteres"
                                data-testid="new-user-password"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Rol del Sistema</Label>
                            <Select value={nuevoUsuario.rol} onValueChange={(v) => setNuevoUsuario({...nuevoUsuario, rol: v})}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="usuario">Usuario</SelectItem>
                                    <SelectItem value="tecnico">Técnico</SelectItem>
                                    <SelectItem value="admin">Administrador</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>Cancelar</Button>
                        <Button onClick={handleCreateUser} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Crear Usuario
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
